import {
  Component,
  Input,
  OnInit,
  signal,
  ViewChild,
  computed,
  OnDestroy,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { Router } from '@angular/router'
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import {
  BehaviorSubject,
  Observable,
  Subscription,
  catchError,
  finalize,
  of,
  switchMap,
} from 'rxjs'
import { ConfirmationService, MenuItem } from 'primeng/api'

// PrimeNG Imports
import { ButtonModule } from 'primeng/button'
import { CardModule } from 'primeng/card'
import { StepsModule } from 'primeng/steps'
import { ConfirmDialogModule } from 'primeng/confirmdialog'
import { ProgressSpinnerModule } from 'primeng/progressspinner'

// Services
import { InterventionsService } from '../../services/interventions.service'
import { InterventionRescheduleService } from '../../services/intervention-reschedule.service'
import { UserService } from '../../../users/services/user.service'
import { ProductsService } from '../../../products/services/products.service'
import { ToastService } from '../../../../shared/services/toast.service'
import { AffairsService } from '../../../affairs/services/affairs.service'

// Models
import { UserModel } from '../../../../core/models/user.model'
import { InterventionEditModel } from '../../../../core/models/intervention-edit.model'
import { AffairModel } from '../../../../core/models/affair.model'

// Components
import { AffairScheduleTechniciansSelectComponent } from '../../../affairs/components/affair-schedule-technicians-select/affair-schedule-technicians-select.component'
import { AffairScheduleTimeSelectComponent } from '../../../affairs/components/affair-schedule-time-select/affair-schedule-time-select.component'
import { AffairScheduleInterventionScheduleComponent } from '../../../affairs/components/affair-schedule-intervention-schedule/affair-schedule-intervention-schedule.component'
import { AffairScheduleProductsSelectComponent } from '../../../affairs/components/affair-schedule-products-select/affair-schedule-products-select.component'
import { AffairScheduleInterventionCreateComponent } from '../../../affairs/components/affair-schedule-intervention-create/affair-schedule-intervention-create.component'
import { InterventionRescheduleProductsComponent } from '../intervention-reschedule-products/intervention-reschedule-products.component'
import { InterventionRescheduleTimeSelectComponent } from '../intervention-reschedule-time-select/intervention-reschedule-time-select.component'

@Component({
  selector: 'app-intervention-reschedule-steps',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    CardModule,
    StepsModule,
    ProgressSpinnerModule,
    ConfirmDialogModule,
    AffairScheduleTechniciansSelectComponent, // Shared technician selection component
    InterventionRescheduleTimeSelectComponent,
    AffairScheduleInterventionScheduleComponent,
    AffairScheduleInterventionCreateComponent,
    InterventionRescheduleProductsComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './intervention-reschedule-steps.component.html',
  styleUrls: ['./intervention-reschedule-steps.component.scss'],
})
export class InterventionRescheduleStepsComponent implements OnInit, OnDestroy {
  @Input() interventionId: number | null = null
  @Input() erpSource: string | null = null

  // Intervention data
  public intervention: InterventionEditModel | null = null
  public loading = true
  public error: string | null = null
  public submitting = false

  // Ajout de la propriété affair complète
  public fullAffair: AffairModel | null = null

  // Step management
  public activeStep = signal(0)
  public steps: MenuItem[] = [
    { label: 'Sélection des produits' },
    { label: 'Sélection du/des technicien(s)' },
    { label: "Sélection d'un créneau horaire" },
    { label: 'Programmation' },
    { label: 'Informations complémentaires' },
  ]

  // Form data
  public scheduleForm: FormGroup
  public interventionForm: FormGroup
  public selectedTechnicians: any[] = []
  public dateRange = signal<Date[]>([])
  public scheduleData: any = null
  public droppedDetails: any = null
  public hasDroppedEvent = false

  // Product management
  public allProducts: any[] = []
  public selectedProducts: any[] = []
  public productGroups: any[] = []
  public loadingProducts = false
  public productsDataReady = false
  public productResetFlag = false

  // UI flags
  public calendarReset = false
  public resetMapFlag = false
  public returningToCalendarStep = false
  public interventionError = ''
  public interventionEventData: any = null

  // User management
  public users$: Observable<UserModel[]>
  public loadingUsers = false
  private filtersSubject = new BehaviorSubject<any>({
    departmentsCode: [],
    search: '',
  })
  public filters$ = this.filtersSubject.asObservable()
  public closestInterventions: any[] = []
  public interventionScheduleUsers: any[] = []
  private loadingInterventionScheduleUsers = false

  // Pre-selected slot from step 2
  public preSelectedSlotData: any = null

  // Contact data
  public contacts: any[] = []
  public affairsContacts: any[] = []
  public worksSupervisors: any[] = []
  private loadingContacts = false

  // Observable management
  private subscriptions: Subscription[] = []

  // Computed property for formatted date range
  public formattedDateRange = computed(() => {
    const [start, end] = this.dateRange()
    if (!start || !end) return 'Non défini'
    return `${this.formatDate(start)} - ${this.formatDate(end)}`
  })

  @ViewChild(AffairScheduleInterventionScheduleComponent)
  interventionScheduleComponent: AffairScheduleInterventionScheduleComponent

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private interventionsService: InterventionsService,
    private rescheduleService: InterventionRescheduleService,
    private usersService: UserService,
    private productsService: ProductsService,
    private toastService: ToastService,
    private confirmationService: ConfirmationService,
    private affairsService: AffairsService // Ajout du service AffairsService
  ) {
    // Setup user filtering
    this.users$ = this.filters$.pipe(
      switchMap((filters) => {
        this.loadingUsers = true
        return this.usersService
          .getUsersWithFilters(filters)
          .pipe(finalize(() => (this.loadingUsers = false)))
      })
    )

    // Initialize forms
    this.initScheduleForm()
    this.initInterventionForm()
  }

  ngOnInit(): void {
    if (this.interventionId && this.erpSource) {
      this.loadInterventionData()
    } else {
      this.error = "Paramètres d'intervention manquants"
      this.loading = false
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe())
  }

  private initScheduleForm(): void {
    this.scheduleForm = this.fb.group({
      startDate: [new Date(), Validators.required],
      duration: [null],
      isTimeSlot: [false],
      startTimeSlot: [null],
      endTimeSlot: [null],
      techniciansIds: [[]],
      userAssistantId: [null],
      isConfirmed: [false],
      dateRange: [this.dateRange()],
    })
  }

  private initInterventionForm(): void {
    this.interventionForm = this.fb.group({
      mlRef: [''],
      name: ['', Validators.required],
      generatedDescription: ['', Validators.required],
      description: [''],
      conformityId: [null],
      worksSupervisorId: [null],
      isDeliveredHouse: [false],
      isWaitingCheck: [false],
      recoverCheck: [false],
      isOffice: [false],
      contacts: [[]],
      affairsContacts: [[]],
      erpSource: [null],
      affairProducts: [[]],
      productData: [null],
    })
  }

  private loadInterventionData(): void {
    this.loading = true

    const sub = this.interventionsService
      .show(this.interventionId!, this.erpSource!)
      .pipe(
        finalize(() => {
          // Ne pas finaliser le chargement ici mais après avoir chargé l'affaire complète
        }),
        catchError((err) => {
          this.error = `Impossible de charger l'intervention: ${err.error?.message || err.message || 'Erreur inconnue'}`
          this.loading = false
          return of(null)
        })
      )
      .subscribe((intervention) => {
        if (intervention) {
          this.intervention = intervention as unknown as InterventionEditModel

          // Set products from intervention
          if (
            this.intervention.affairProducts &&
            this.intervention.affairProducts.length > 0
          ) {
            this.selectedProducts = [...this.intervention.affairProducts]
          }

          // Récupérer l'affaire complète avec le mlRef
          if (this.intervention.affair?.mlRef) {
            this.loadFullAffairData(this.intervention.affair.mlRef)
          } else {
            this.loading = false
            this.error = "Information d'affaire manquante dans l'intervention"
          }
        } else {
          this.loading = false
        }
      })

    this.subscriptions.push(sub)
  }

  /**
   * Charge les données complètes de l'affaire à partir de son mlRef
   * @param mlRef Référence ML de l'affaire à charger
   */
  private loadFullAffairData(mlRef: string): void {
    this.loading = true // Maintenir l'état de chargement pendant la récupération

    const affairSub = this.affairsService
      .getAffairById(mlRef)
      .pipe(
        finalize(() => {
          this.loading = false // Terminer le chargement une fois toutes les opérations complétées
        }),
        catchError((err) => {
          console.error("Erreur lors du chargement de l'affaire complète:", err)
          // Continuer avec les données partielles de l'affaire incluses dans l'intervention
          this.populateFormWithIntervention()
          if (this.intervention?.affair?.mlRef) {
            this.loadAllProducts(this.intervention.affair.mlRef)
          }
          return of(null)
        })
      )
      .subscribe((affair) => {
        if (affair) {
          this.fullAffair = affair

          // Conserver les informations spécifiques de l'intervention tout en enrichissant avec les données complètes de l'affaire
          if (this.intervention && this.intervention.affair) {
            // Mettre à jour l'objet affair de l'intervention avec les données complètes
            this.intervention.affair = {
              ...this.intervention.affair,
              ...affair,
            }
          }

          // Continuer l'initialisation en gardant l'indicateur de chargement
          this.populateFormWithIntervention()
          this.loadAllProducts(mlRef)
        }
      })

    this.subscriptions.push(affairSub)
  }

  private loadAllProducts(mlRef: string): void {
    // Load products for the affair
    this.loadingProducts = true
    this.productsService
      .getSchedulableProductsByMlRef(mlRef, true)
      .pipe(
        finalize(() => {
          this.loadingProducts = false
          this.productsDataReady = true
        })
      )
      .subscribe({
        next: (products) => {
          this.allProducts = products

          // Maintenant qu'on a tous les produits, on peut procéder au traitement complet
          this.ensureSelectedProductsAreIncluded()

          // Traitement des produits sélectionnés seulement une fois
          if (this.selectedProducts?.length > 0) {
            this.ensureCompleteProductData()
            this.createProductGroupFromSelection()
            this.onProductSelectionChanged(this.selectedProducts)
          }
        },
        error: (error) => {
          console.error('Error loading products:', error)
          this.productsDataReady = true
        },
      })
  }

  private populateFormWithIntervention(): void {
    if (!this.intervention) return

    // Ne pas initialiser les techniciens sélectionnés
    // Modification: Ne pas copier les techniciens existants
    // this.selectedTechnicians = this.intervention.usersTechnicians || []

    // Au lieu de cela, initialiser avec un tableau vide pour forcer la sélection
    this.selectedTechnicians = []

    // Set date range
    if (this.intervention.startDate) {
      const startDate = new Date(this.intervention.startDate)

      // Calculer le lundi de la semaine suivant la date de l'intervention
      const nextMondayAfterIntervention = this.getNextMondayAfterDate(startDate)

      // Calculer le dimanche à la fin de cette semaine
      const nextSundayAfterIntervention = new Date(nextMondayAfterIntervention)
      nextSundayAfterIntervention.setDate(
        nextMondayAfterIntervention.getDate() + 6
      )

      // Normaliser les heures
      nextMondayAfterIntervention.setHours(0, 0, 0, 0)
      nextSundayAfterIntervention.setHours(23, 59, 59, 999)

      // Définir la plage de dates comme étant la semaine après la date de l'intervention
      this.dateRange.set([
        nextMondayAfterIntervention,
        nextSundayAfterIntervention,
      ])
    }

    // Initialize schedule form based on intervention
    const isTimeSlot = this.intervention.isTimeSlot || false

    if (isTimeSlot) {
      // Time slot scheduling
      const startTimeSlot = this.intervention.startTimeSlot
        ? this.parseTimeString(this.intervention.startTimeSlot)
        : null

      const endTimeSlot = this.intervention.endTimeSlot
        ? this.parseTimeString(this.intervention.endTimeSlot)
        : null

      this.scheduleForm.patchValue({
        startDate: new Date(this.intervention.startDate),
        isTimeSlot: true,
        startTimeSlot,
        endTimeSlot,
        // Modification: Ne pas initialiser les IDs des techniciens
        // techniciansIds: this.selectedTechnicians.map((tech) => tech.id),
        techniciansIds: [], // Tableau vide pour forcer la sélection
        userAssistantId: this.intervention.userAssistant?.id || null,
        isConfirmed: true,
      })
    } else {
      // Duration-based scheduling
      const duration = this.calculateDuration(
        this.intervention.startDate,
        this.intervention.endDate
      )

      this.scheduleForm.patchValue({
        startDate: new Date(this.intervention.startDate),
        isTimeSlot: false,
        duration: this.createTimeFromMinutes(duration),
        // Modification: Ne pas initialiser les IDs des techniciens
        // techniciansIds: this.selectedTechnicians.map((tech) => tech.id),
        techniciansIds: [], // Tableau vide pour forcer la sélection
        userAssistantId: this.intervention.userAssistant?.id || null,
        isConfirmed: true,
      })
    }

    // Create draggable event data for calendar
    this.createDroppedEventFromIntervention()

    // Update intervention form
    this.updateInterventionFormWithData()
  }

  private updateInterventionFormWithData(): void {
    if (!this.intervention) return

    console.log('Updating intervention form with data:', this.intervention)

    this.interventionForm.patchValue({
      mlRef: this.intervention.affair?.mlRef || '',
      name: this.intervention.name || '',
      generatedDescription: this.intervention.generatedDescription || '',
      description: this.intervention.description || '',
      conformityId: this.intervention.conformityId || null,
      worksSupervisorId: this.intervention.worksSupervisorId || null,
      isDeliveredHouse: this.intervention.isDeliveredHouse || false,
      isWaitingCheck: this.intervention.isWaitingCheck === 1 ? true : false,
      recoverCheck: this.intervention.recoverCheck === 1 ? true : false,
      isOffice: this.intervention.isOffice === 1 ? true : false,
      contacts: this.intervention.contacts.map((x) => x.id) || [],
      affairContacts: this.intervention.affairsContacts.map((x) => x.id) || [],
      erpSource: this.intervention.erpSource || null,
    })

    // Set products data
    if (this.intervention.affairProducts?.length) {
      const affairProducts = this.intervention.affairProducts.map(
        (product) => ({
          affairProductId: product.id,
          productId: product.productId,
          quantity: product.quantity || 1,
          erpSource: this.intervention?.erpSource,
        })
      )

      this.interventionForm.patchValue({ affairProducts })
    }
  }

  // Méthodes pour les étapes de navigation
  nextStep(): void {
    if (this.activeStep() < this.steps.length - 1) {
      // Validation pour chaque étape
      if (this.activeStep() === 0) {
        if (!this.selectedProducts || this.selectedProducts.length === 0) {
          this.toastService.showError(
            'Veuillez sélectionner au moins un produit'
          )
          return
        }
      }

      if (this.activeStep() === 1) {
        if (!this.isTechniciansSelected) {
          this.toastService.showError(
            'Veuillez sélectionner au moins un technicien'
          )
          return
        }

        // Si nous passons de l'étape 1 à l'étape 2 (calendrier), assurons-nous que
        // l'événement à glisser-déposer nécessite explicitement un glisser-déposer manuel
        if (this.droppedDetails) {
          this.droppedDetails = {
            ...this.droppedDetails,
            requireDragDrop: true,
            isAlreadyInCalendar: false,
          }

          // Réinitialiser le flag pour éviter que l'événement ne soit automatiquement placé
          this.hasDroppedEvent = false
        }
      }

      if (this.activeStep() === 2 && !this.hasDroppedEvent) {
        this.toastService.showError(
          "Veuillez d'abord déposer un produit sur le calendrier"
        )
        return
      }

      if (this.activeStep() === 3) {
        if (!this.interventionScheduleComponent?.isFormValid()) {
          this.toastService.showError(
            'Veuillez remplir correctement les informations de programmation'
          )
          return
        }
        this.scheduleData = this.interventionScheduleComponent.getFormData()
      }

      if (this.activeStep() === 4) {
        if (!this.interventionForm.valid) {
          this.toastService.showError(
            'Veuillez remplir tous les champs obligatoires'
          )
          return
        }
      }

      // Avancer d'une étape
      this.activeStep.update((step) => step + 1)

      // Traitement spécial pour l'étape du calendrier
      if (this.activeStep() === 2) {
        this.returningToCalendarStep = true
        setTimeout(() => {
          this.returningToCalendarStep = false
        }, 500)
      }
    }
  }

  previousStep(): void {
    if (this.activeStep() > 0) {
      this.activeStep.update((step) => step - 1)
    }
  }

  submitReschedule(): void {
    if (!this.intervention || !this.scheduleData) {
      this.toastService.showError('Données de programmation incomplètes')
      return
    }

    if (!this.interventionForm.valid) {
      this.interventionError = 'Veuillez remplir tous les champs obligatoires'
      return
    }

    this.submitting = true

    const scheduleData = this.scheduleData
    const additionalData = this.interventionForm.value

    const submissionData = {
      mlRef: this.intervention.affair.mlRef,
      interventionId: this.intervention.id,
      originalErpSource: this.intervention.erpSource,
      erpSource: this.resolveErpSource(
        this.selectedTechnicians[0]?.erpSource || this.intervention.erpSource
      ),
      startDate: this.formatDateTimePreserveLocal(scheduleData.startDate),
      techniciansIds: scheduleData.techniciansIds,
      userAssistantId: scheduleData.userAssistantId,
      isConfirmed: scheduleData.isConfirmed,
      // TimeSlot or duration data
      ...(scheduleData.isTimeSlot
        ? {
            isTimeSlot: true,
            startTimeSlot: this.formatTime(scheduleData.startTimeSlot),
            endTimeSlot: this.formatTime(scheduleData.endTimeSlot),
            duration: this.formatTime(scheduleData.duration),
          }
        : {
            isTimeSlot: false,
            duration: this.formatTime(scheduleData.duration),
          }),
      // Additional information
      name: additionalData.name,
      generatedDescription: additionalData.generatedDescription,
      description: additionalData.description,
      conformityId: additionalData.conformityId,
      worksSupervisorId: additionalData.worksSupervisorId,
      isDeliveredHouse: additionalData.isDeliveredHouse,
      isWaitingCheck: additionalData.isWaitingCheck,
      recoverCheck: additionalData.recoverCheck,
      isOffice: additionalData.isOffice,
      contacts: additionalData.contacts,
      affairContacts: additionalData.affairsContacts,
      affairProducts: additionalData.affairProducts,
    }

    // Send the replanification request
    this.interventionsService.reschedule(submissionData).subscribe({
      next: () => {
        this.toastService.showSuccess('Intervention replanifiée avec succès')
        this.router.navigate(['/affair', this.intervention?.affair.mlRef])
      },
      error: (error) => {
        this.toastService.showError('Erreur lors de la replanification')
        this.submitting = false
      },
      complete: () => {
        this.submitting = false
      },
    })
  }

  resetForm(): void {
    this.confirmationService.confirm({
      message:
        'Êtes-vous sûr de vouloir réinitialiser le formulaire ? Toutes les modifications seront perdues.',
      header: 'Confirmation de réinitialisation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Oui, réinitialiser',
      rejectLabel: 'Non',
      accept: () => {
        this.calendarReset = true
        this.resetMapFlag = true
        this.productResetFlag = true
        this.activeStep.set(0)

        // Reset to original intervention data
        this.populateFormWithIntervention()

        // Reset products
        if (
          this.intervention?.affairProducts &&
          this.intervention.affairProducts.length > 0
        ) {
          this.selectedProducts = [...this.intervention.affairProducts]
        }

        setTimeout(() => {
          this.calendarReset = false
          setTimeout(() => {
            this.resetMapFlag = false
            this.productResetFlag = false
          }, 200)
        }, 500)
      },
    })
  }

  cancelReschedule(): void {
    this.confirmationService.confirm({
      message: 'Êtes-vous sûr de vouloir annuler la replanification ?',
      header: "Confirmation d'annulation",
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Oui, annuler',
      rejectLabel: 'Non',
      accept: () => {
        this.router.navigate([
          '/intervention',
          this.intervention?.id,
          'view',
          this.intervention?.erpSource,
        ])
      },
    })
  }

  // Event handlers for child components
  onTechniciansSelected(technicians: UserModel[]): void {
    const hadTechnicians =
      this.selectedTechnicians && this.selectedTechnicians.length > 0
    const hasTechnicians = technicians && technicians.length > 0

    this.selectedTechnicians = technicians

    this.scheduleForm.patchValue({
      techniciansIds: technicians.map((tech) => tech.id),
    })

    // Load contacts only when going from 0 to some technicians
    if (hasTechnicians && !hadTechnicians) {
      this.loadContactSelects()
    } else if (!hasTechnicians && hadTechnicians) {
      this.contacts = []
      this.affairsContacts = []
    }
  }

  onClosestInterventionsChanged(interventions: any[]): void {
    this.closestInterventions = interventions
  }

  /**
   * Gère la pré-sélection d'un créneau horaire depuis l'étape 2
   * @param slotData Les données du créneau pré-sélectionné
   */
  onSlotPreSelected(slotData: any): void {
    if (slotData) {
      // Stocker les données du créneau pré-sélectionné
      this.preSelectedSlotData = slotData

      // Effacer les détails de drop existants car le créneau pré-sélectionné les remplace
      this.droppedDetails = null
      this.hasDroppedEvent = false

      console.log('Créneau pré-sélectionné, détails de drop effacés:', slotData)
    } else {
      // Effacer le créneau pré-sélectionné
      this.preSelectedSlotData = null
    }
  }

  onDateRangeSelected(dates: Date[]): void {
    if (!dates || !Array.isArray(dates) || dates.length !== 2) return

    try {
      const start = this.createValidDate(dates[0])
      const end = this.createValidDate(dates[1])

      if (!start || !end) return

      start.setHours(0, 0, 0, 0)
      end.setHours(23, 59, 59, 999)

      this.dateRange.set([start, end])

      // Propager cette plage de dates aux autres composants si besoin
      if (this.activeStep() === 1) {
        // Nous sommes dans l'étape des techniciens, mettons à jour la plage de dates dans le formulaire scheduleForm
        this.scheduleForm.patchValue({
          dateRange: [start, end],
        })
      }
    } catch (error) {
      console.error('Error processing dates:', error)
    }
  }

  onDropDetails(event: any): void {
    if (!event) {
      this.droppedDetails = null
      this.hasDroppedEvent = false
      return
    }

    this.droppedDetails = event
    this.hasDroppedEvent = !!event

    if (this.scheduleForm && event?.date) {
      this.scheduleForm.patchValue({
        startDate: event.date,
      })

      if (event?.duration) {
        const hours = Math.floor(event.duration / 60)
        const minutes = event.duration % 60

        const durationDate = new Date()
        durationDate.setHours(hours)
        durationDate.setMinutes(minutes)
        durationDate.setSeconds(0)

        this.scheduleForm.patchValue({
          duration: durationDate,
        })
      }
    }
  }

  onProductSelectionChanged(products: any[]): void {
    if (!products || products.length === 0) {
      if (Array.isArray(products)) {
        this.selectedProducts = []
      }
      return
    }

    // Get ERP source
    const erpSource = this.erpSource || this.intervention?.erpSource || 'mylink'
    const idField = `id${erpSource.charAt(0).toUpperCase()}${erpSource.slice(1)}`

    // Map selected products to full data
    this.selectedProducts = products.map((selectedProduct) => {
      let fullProduct = this.allProducts.find(
        (p) => p.id === selectedProduct.id
      )

      if (!fullProduct && selectedProduct[idField]) {
        fullProduct = this.allProducts.find(
          (p) => p[idField] === selectedProduct[idField]
        )
      }

      if (fullProduct) {
        return {
          ...fullProduct,
          quantity: selectedProduct.quantity || 1,
        }
      }

      return selectedProduct
    })

    // Ensure complete data
    this.ensureCompleteProductData()

    // Update filters for technician view
    const productNames = this.selectedProducts.map((p) => p.product?.name || '')
    this.filtersSubject.next({
      ...this.filtersSubject.value,
      productsNames: productNames,
    })

    // Update dropped event with current products
    this.updateDroppedDetailsWithProducts()
  }

  onProductGroupCreated(group: any): void {
    if (!group) return
    this.productGroups = [group]
  }

  get isTechniciansSelected(): boolean {
    return this.selectedTechnicians.length > 0
  }

  getSelectedProductIds(): number[] {
    if (!this.productsDataReady || !this.allProducts.length) {
      return []
    }

    if (this.selectedProducts && this.selectedProducts.length > 0) {
      const erpSource =
        this.erpSource || this.intervention?.erpSource || 'mylink'
      const idField = `id${erpSource.charAt(0).toUpperCase()}${erpSource.slice(1)}`

      return this.selectedProducts.map((product) => {
        const completeProduct = this.allProducts.find(
          (p) =>
            p.id === product.id ||
            (product[idField] && p[idField] === product[idField])
        )
        return completeProduct ? completeProduct.id : product.id
      })
    }

    return this.intervention?.affairProducts?.map((p) => p.id) || []
  }

  private loadContactSelects(): void {
    if (!this.selectedTechnicians?.length || this.loadingContacts) return
    if (!this.intervention?.affair?.mlRef) return

    this.loadingContacts = true
    const formValue = {
      mlRef: this.intervention.affair.mlRef,
      erpSource: this.intervention.affair.erpSource,
    }

    this.interventionsService
      .getSelectsForCreate(formValue)
      .pipe(finalize(() => (this.loadingContacts = false)))
      .subscribe({
        next: (data) => {
          if (data) {
            this.contacts = data.contacts || []
            this.affairsContacts = data.affairContacts || []
            this.worksSupervisors = data.worksSupervisors || []

            // Restore current form values
            const currentValues = {
              contacts: this.interventionForm.get('contacts')?.value,
              affairsContacts:
                this.interventionForm.get('affairsContacts')?.value,
              worksSupervisorId:
                this.interventionForm.get('worksSupervisorId')?.value,
            }

            if (
              currentValues.contacts ||
              currentValues.affairsContacts ||
              currentValues.worksSupervisorId
            ) {
              setTimeout(() => {
                this.interventionForm.patchValue(currentValues)
              }, 100)
            }
          }
        },
        error: (err) => {
          console.error('Error loading contacts:', err)
        },
      })
  }

  private ensureSelectedProductsAreIncluded(): void {
    if (!this.selectedProducts?.length || !this.allProducts?.length) return

    const erpSource = this.erpSource || this.intervention?.erpSource || 'mylink'
    if (!erpSource) {
      console.error('ERP source is missing')
      return
    }

    const idField = `id${erpSource.charAt(0).toUpperCase()}${erpSource.slice(1)}`

    // Build sets of existing IDs
    const existingProductIds = new Set()
    const erpProductIds = new Set()

    this.allProducts.forEach((product) => {
      existingProductIds.add(product.id)

      if (product[idField]) {
        erpProductIds.add(product[idField])
      }

      ;['idAbm', 'idNrj', 'idMylink'].forEach((field) => {
        if (product[field]) {
          existingProductIds.add(product[field])
        }
      })
    })

    // Map for direct product lookups
    const existingProductMap = new Map(
      this.allProducts.map((product) => [product.id, product])
    )

    // Check and add missing products
    const updatedSelected = this.selectedProducts.map((selectedProduct) => {
      let matchedProduct = existingProductMap.get(selectedProduct.id)

      if (!matchedProduct) {
        const productErpId = selectedProduct[idField]
        const isDuplicate =
          existingProductIds.has(selectedProduct.id) ||
          (productErpId && erpProductIds.has(productErpId))

        if (!isDuplicate) {
          this.allProducts.push(selectedProduct)
          existingProductIds.add(selectedProduct.id)

          if (productErpId) {
            erpProductIds.add(productErpId)
          }

          return selectedProduct
        } else {
          const matchingProduct = this.allProducts.find(
            (p) =>
              p.id === selectedProduct.id ||
              (productErpId && p[idField] === productErpId)
          )

          return matchingProduct || selectedProduct
        }
      }

      return matchedProduct
    })

    this.selectedProducts = updatedSelected
    this.allProducts = this.deduplicateProducts(this.allProducts, idField)
  }

  private ensureCompleteProductData(): void {
    if (!this.selectedProducts?.length) return

    const erpSource = this.erpSource || this.intervention?.erpSource || 'mylink'

    // Map products by ID
    const productsMap = new Map<number, any>()
    const erpProductsMap = new Map<number, any>()

    this.allProducts.forEach((product) => {
      productsMap.set(product.id, product)
      ;['idAbm', 'idNrj', 'idMylink'].forEach((field) => {
        if (product[field]) {
          erpProductsMap.set(product[field], product)
        }
      })
    })

    // Replace each selectedProduct with complete data
    this.selectedProducts = this.selectedProducts.map((selectedProduct) => {
      let fullProduct = productsMap.get(selectedProduct.id)

      if (!fullProduct) {
        fullProduct = erpProductsMap.get(selectedProduct.id)
      }

      if (fullProduct) {
        return {
          ...fullProduct,
          quantity: selectedProduct.quantity || 1,
        }
      }

      return selectedProduct
    })
  }

  private deduplicateProducts(products: any[], idField: string): any[] {
    const uniqueMap = new Map()

    products.forEach((product) => {
      if (!uniqueMap.has(product.id)) {
        uniqueMap.set(product.id, product)
      }

      if (product[idField] && !uniqueMap.has(`erp_${product[idField]}`)) {
        uniqueMap.set(`erp_${product[idField]}`, product)
      }

      ;['idAbm', 'idNrj', 'idMylink'].forEach((field) => {
        if (product[field]) {
          uniqueMap.set(`erp_${product[field]}`, product)
        }
      })
    })

    return Array.from(uniqueMap.values())
  }

  private createProductGroupFromSelection(): void {
    if (!this.selectedProducts?.length) return

    // Ensure all products have complete data
    const hasIncompleteProducts = this.selectedProducts.some(
      (p) => !p.product || !p.product.name
    )

    if (hasIncompleteProducts) {
      this.ensureCompleteProductData()

      if (this.selectedProducts.some((p) => !p.product || !p.product.name)) {
        return
      }
    }

    // Create unique group ID
    const timestamp = Date.now()
    const productIdsList = this.selectedProducts
      .map((p) => p.id)
      .sort()
      .join('_')
    const groupId = `group_${timestamp}_${productIdsList}`

    // Get ERP source - use fullAffair if available
    const erpSource =
      this.erpSource ||
      this.intervention?.erpSource ||
      this.fullAffair?.erpSource ||
      'mylink'

    // Ensure all products have complete data
    const productsWithData = this.selectedProducts.map((p) => {
      if (!p.product || !p.product.name) {
        const fullProduct = this.allProducts.find((fp) => fp.id === p.id)

        if (fullProduct) {
          return fullProduct
        }

        return {
          ...p,
          product: p.product || {
            name: 'Produit',
            duration: p.duration || 30,
          },
        }
      }

      return p
    })

    // Create product group
    this.productGroups = [
      {
        id: groupId,
        products: JSON.parse(JSON.stringify(productsWithData)), // Deep copy
        productsNames: this.formatGroupName(productsWithData),
        totalDuration: productsWithData.reduce(
          (sum, p) => sum + (p.product?.duration || 0),
          0
        ),
        isInitialGroup: true,
        erpSource,
      },
    ]
  }

  private updateDroppedDetailsWithProducts(): void {
    if (!this.selectedProducts.length) return

    this.ensureCompleteProductData()

    const totalDuration = this.selectedProducts.reduce(
      (sum, product) => sum + (product.product?.duration || 0),
      0
    )

    const erpSource = this.intervention?.erpSource || 'mylink'

    const eventDate =
      this.interventionEventData?.date ||
      (this.intervention?.startDate
        ? new Date(this.intervention.startDate)
        : new Date())

    this.droppedDetails = {
      title: this.intervention?.name || 'Intervention à reprogrammer',
      date: eventDate,
      productsData:
        this.selectedProducts.length > 1 ? [...this.selectedProducts] : null,
      productData:
        this.selectedProducts.length === 1 ? this.selectedProducts[0] : null,
      duration: totalDuration,
      erpSource,
      uniqueId: `intervention_${this.intervention?.id}_${Date.now()}`,
      isAlreadyInCalendar: false,
      requireDragDrop: true,
    }

    this.hasDroppedEvent = true
  }

  private createDroppedEventFromIntervention(): void {
    if (!this.intervention) return

    this.interventionEventData = {
      id: this.intervention.id,
      date: new Date(this.intervention.startDate),
      title: this.intervention.name,
      productData:
        this.intervention.affairProducts?.length === 1
          ? this.intervention.affairProducts[0]
          : null,
      productsData:
        this.intervention.affairProducts?.length > 1
          ? this.intervention.affairProducts
          : this.intervention.affairProducts, // Toujours passer les produits même s'il n'y en a qu'un
      duration: this.calculateDuration(
        this.intervention.startDate,
        this.intervention.endDate
      ),
      requireDragDrop: true, // Marquer explicitement que cet événement nécessite un glisser-déposer
    }

    this.droppedDetails = {
      ...this.interventionEventData,
      uniqueId: `intervention_${this.intervention.id}_${Date.now()}`,
      isAlreadyInCalendar: false,
      interventionId: this.intervention.id,
      erpSource: this.intervention.erpSource,
      requireDragDrop: true, // S'assurer que cette propriété est également définie ici
    }

    // Ne pas définir hasDroppedEvent à true initialement pour forcer le glisser-déposer
    this.hasDroppedEvent = false
  }

  private formatDate(date: Date): string {
    return `${date.getFullYear()}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
  }

  private createValidDate(input: any): Date | null {
    if (!input) return null

    try {
      const date = new Date(input)
      return isNaN(date.getTime()) ? null : date
    } catch (e) {
      return null
    }
  }

  private formatDateTimePreserveLocal(date: Date): string {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return ''

    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    const seconds = date.getSeconds().toString().padStart(2, '0')

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
  }

  private formatTime(date: Date): string {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return ''

    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')

    return `${hours}:${minutes}`
  }

  private calculateDuration(startDate: string, endDate: string): number {
    const start = new Date(startDate)
    const end = new Date(endDate)

    return Math.round((end.getTime() - start.getTime()) / 60000)
  }

  private parseTimeString(timeString: string): Date {
    const [hours, minutes] = timeString.split(':').map(Number)
    const date = new Date()
    date.setHours(hours, minutes, 0, 0)

    return date
  }

  private createTimeFromMinutes(minutes: number): Date {
    const date = new Date()
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60

    date.setHours(hours, mins, 0, 0)

    return date
  }

  formatDisplayDate(date: string): string {
    if (!date) return 'Date non définie'

    const d = new Date(date)
    if (isNaN(d.getTime())) return 'Date invalide'

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }

    return new Intl.DateTimeFormat('fr-FR', options).format(d)
  }

  getDisplayDuration(): string {
    if (!this.intervention?.startDate || !this.intervention?.endDate) {
      return 'Durée non définie'
    }

    const duration = this.calculateDuration(
      this.intervention.startDate,
      this.intervention.endDate
    )

    const hours = Math.floor(duration / 60)
    const minutes = duration % 60

    if (hours === 0) {
      return `${minutes} min`
    } else if (minutes === 0) {
      return `${hours} h`
    } else {
      return `${hours} h ${minutes} min`
    }
  }

  private formatGroupName(products: any[]): string {
    if (!products?.length) return 'Groupe de produits'

    return (
      products
        .map((p) => p.product?.name || 'Produit')
        .join(', ')
        .substring(0, 60) + (products.length > 2 ? '...' : '')
    )
  }

  updateFilters(filters: any): void {
    this.filtersSubject.next({
      ...this.filtersSubject.value,
      ...filters,
    })
  }

  /**
   * Calcule le lundi de la semaine suivant une date donnée
   * @param date La date de référence
   * @returns Le prochain lundi après cette date
   */
  private getNextMondayAfterDate(date: Date): Date {
    const dayOfWeek = date.getDay() // 0 = dimanche, 1 = lundi, etc.

    // Calculer le nombre de jours jusqu'au prochain lundi
    // Si c'est un lundi (1), ajouter 7 jours pour passer à la semaine suivante
    // Sinon, ajouter le nombre de jours nécessaires pour atteindre le lundi suivant
    const daysUntilNextMonday = dayOfWeek === 1 ? 7 : (8 - dayOfWeek) % 7

    // Créer une nouvelle date pour éviter de modifier l'originale
    const nextMonday = new Date(date)
    nextMonday.setDate(date.getDate() + daysUntilNextMonday)

    return nextMonday
  }

  /**
   * Résout l'erpSource en forçant 'nrj' si l'affaire est de type inspection (marketTypeId === 5)
   */
  private resolveErpSource(defaultSource: string): string {
    if (this.fullAffair?.marketType?.id === 5) {
      return 'nrj'
    }
    return defaultSource
  }
}
